import PerfectScrollbar from "perfect-scrollbar";
import HcsProgressbar from "./controls/hcs-progressbar";
import objectAssignDeep from "./tools/object-assign-deep";

/**
 * @class
 * @classdesc Handles parts list.
 */
export default class HcsPartsList
{
    constructor(paramObj) {
        let self = this;

        this.hcs = paramObj.hcs;
        this.container = this.hcs.container.querySelector(".hcs-parts-list");

        this.productSplitOptions = this.hcs.settings.get("SETTINGS.PRODUCT_ID_CONVERTER", "json") || null;
        this.visibleBomItems = this.hcs.settings.get("SETTINGS.PARTS_LIST_VISIBLE_BOM_ITEMS", "int") || 0;

        this.instance(".hcs-close").addEventListener("click", _ => {
            self.close();
        });

        this.instance(".hcs-parts-list-download").addEventListener("click", _ => {
            let includePrice = self.instance(".hcs-parts-list-toggle-price").checked;

            self.hcs.cfgEngine.createOverviewPdf({ includePrice: includePrice });
        });

        this.scrollbarMain = new PerfectScrollbar(this.instance(".hcs-content"), {
            suppressScrollX: true
        });

        self.progressBar = new HcsProgressbar({
            hcs: this.hcs,
            container: this.instance()
        });

        this.hcs.ui.onUiResized(_ => {
            this.scrollbarMain.update();
        });

        this.getLocalProductData = this.getLocalProductData.bind(this);
    }

    /**
     * Read thumbnail specs from template.
     * @returns {object} Thumbnail size and color specifications.
     */
    get thumbnailSpecs() {
        return {
            width: this.hcs.api.instance(".hcs-thumbnail-template").clientWidth,
            height: this.hcs.api.instance(".hcs-thumbnail-template").clientHeight,
            bgColor: this.hcs.api.rgbaToColorCode(window.getComputedStyle(this.hcs.api.instance(".hcs-thumbnail-template"), null).getPropertyValue("background-color")).replace("#", "0x")
        };
    }

    /**
     * Open the parts list dialog.
     */
    show() {
        if(!this.hcs.api.checkFunction("partsList")) {
            return;
        }

        let partsListVisible = this.hcs.api.instance().classList.contains("hcs-parts-list-visible");

        if(partsListVisible) {
            this.hcs.api.instance().classList.remove("hcs-parts-list-visible");
            this.hcs.api.instance(".hcs-button-parts-list").classList.remove("hcs-checked");
            return;
        }

        this.hcs.api.instance().classList.add("hcs-parts-list-visible");
        this.hcs.api.instance(".hcs-button-parts-list").classList.add("hcs-checked");

        this.refresh();
    }

    /**
     * Reload parts list while still visible.
     * Only updates when parts list is visible.
     */
    refresh() {
        let self = this;

        if(!this.hcs.api.instance().classList.contains("hcs-parts-list-visible")) {
            return;
        }

        this.clear();

        this.getData({
            showSpinner: true,
            showErrorMessage: false
        }).then((result) => {
            if(result.success) {
                self.hcs.api.logToolbarOption("Parts List");

                if(result.ecs && result.ecs !== "") {
                    self.update(result).then(_ => {
                        try {
                            self.instance(".hcs-custom-content.hcs-header").innerHTML = "";
                            self.instance(".hcs-custom-content.hcs-footer").innerHTML = "";

                            self.hcs.cfgEngine.partsListCustomContent.call(self, {
                                header: self.instance(".hcs-custom-content.hcs-header"),
                                footer: self.instance(".hcs-custom-content.hcs.footer")
                            });
                        } catch(e) {
                            self.hcs.api.showError({
                                title: "Error while creating custom parts list content",
                                message: e
                            });
                        }

                        self.hcs.api.waitImagesLoaded(self.container.querySelectorAll(".hcs-tbl-body img")).then(_ => {
                            self.scrollbarMain.update();
                        }).catch(e => {
                            console.error(e);
                        });
                    });
                }
            }
        }).catch((result) => {
            self.hcs.api.showError({
                title: "Could not read product information",
                messageMap: "jsError",
                message: typeof (result) === "string" ? {
                    message: result
                } : (result.error ? result.error : result)
            });
        });
    }

    /**
     * Converts given item list to Haefele shop request object.
     * @param {Array} partsList - List of parts which must include the following properties per item.
     * The "quantity" property is optional. We use 1 when not set.
     * @example
     * [
     *    {
     *       productId: "123.45.23",
     *       quantity: 10
     *    },
     *    {
     *       productId: "123.45.66",
     *       quantity: 2
     *    }
     *    ....
     * ]
     * @returns {Array} JSON object formatted for Haefele shop request.
     */
    toShopRequestObject(partsList) {
        let self = this;

        return {
            productIdentifierInformation: partsList.map((i, index) => {
                return {
                    articleNumber: i.productId.replace(/\./g, ""),
                    requestedQuantity: i.quantity ? i.quantity : 1,
                    requestedDeliveryDate: null,
                    priceType: self.hcs.cart.priceType,
                    shopPosNumber: index
                };
            })
        };
    }

    /**
     * Read parts list data from ECS.
     * @param {Object} [options] - Additional options
     * @param {bool} [options.priceRequest=true] - Request shop for pricing data, when "false" only build parts list.
     * @param {bool} [options.showSpinner=false] - Set true to show loading spinner while parts list data is being retrieved from ECS.
     * @returns {Promise} Promise object when data request has finished.
     */
    getData(options) {
        let self = this;

        let opts = Object.assign({}, {
            priceRequest: true,
            logRequest: false,
            showSpinner: false,
            showErrorMessage: true
        }, options);

        let loading = true;

        return new Promise((resolve, reject) => {
            setTimeout(_ => {
                if(loading && opts.showSpinner) {
                    self.progressBar.progress("indeterminate");
                }
            }, 200);

            // Before exiting main Promise ensure to hide possible running progress bar.
            let promiseProgressGate = {
                pResolve: data => {
                    if(opts.showSpinner) {
                        self.progressBar.progress(false);
                        loading = false;
                    }

                    resolve(data);
                },
                pReject: data => {
                    if(opts.showSpinner) {
                        self.progressBar.progress(false);
                        loading = false;
                    }

                    reject(data);
                }
            };

            self.hcs.api.callback({
                url: self.hcs.api.ecs({
                    method: "getBom",
                    params: {
                        "configurationId": self.hcs.configurationId
                    }
                }),
                showErrorMessage: opts.showErrorMessage
            }).then(ecsGetBomResult => {
                let bomResult = objectAssignDeep({}, {
                    success: true,
                    totalPrice: 0,
                    items: [],
                    error: {
                        message: null,
                        details: null
                    }
                }, ecsGetBomResult);

                let overallQuantity = self.hcs.cfgEngine.partsListOverallQuantity.call(self);

                let funcBuildPartsList = (ecsBomObject, shopResultResolved, variationAttributesList, userNotLoggedIn) => {
                    bomResult.items = ecsBomObject.oBomData.aGroupedDatasets[0].aDatasets.map((bomItem, index) => {
                        //let shopDataItem = shopResultResolved.ProductInformations.filter((shopItem) => { return bomItem.sArtNr === shopItem.articleNumber && parseInt(shopItem.shopPosNumber) === index; })[0];

                        // 20191014_ARTNR_COMPARISON
                        // When user is not logged-in shop does not return the "articleNumber" value in JSON result but
                        // only "articleNumberFormatted". To compare product numbers with those from ECS BoM
                        // we must first remove all dots in "articleNumberFormatted".
                        // When this has been solved in Häfele shop remove the block below and uncomment the line above.
                        let shopDataItem = shopResultResolved.ProductInformations.filter(shopItem => {
                            var shopPosNumber = parseInt(shopItem.shopPosNumber);
                            return bomItem.sArtNr.replace(/\./g, "") === (shopItem.articleNumberFormatted || shopItem.articleNumber).replace(/\./g, "") && shopPosNumber === index;
                        })[0];

                        let singlePrice = (shopDataItem && shopDataItem.singlePrice) ? shopDataItem.singlePrice.value / shopDataItem.priceUnit : 0;
                        let totalPrice = (shopDataItem && shopDataItem.totalPrice) ? shopDataItem.totalPrice.value : 0;
                        let articleDescription = shopDataItem ? (shopDataItem.articleDisplayName ? shopDataItem.articleDisplayName : shopDataItem.articleName) : bomItem.sDescription;
                        let ecsVarsItem = variationAttributesList ? variationAttributesList.find(i => i.shopPosNumber === index) : null;

                        return {
                            productId: shopDataItem ? shopDataItem.articleNumberFormatted : bomItem.sArtNr,
                            displayName: articleDescription, /* is required for PDF parts list for Eval() function. */
                            description: shopDataItem ? shopDataItem.articleDescription : null,
                            thumbnail: shopDataItem ? `<img src="${shopDataItem.primaryImage.thumbnailUrl}" org="${shopDataItem.primaryImage.defaultUrl}" />` : "",
                            image: shopDataItem ? shopDataItem.primaryImage.thumbnailUrl : null,
                            unit: (shopDataItem && shopDataItem.quantityUnitName) ? shopDataItem.quantityUnitName : "",
                            quantity: bomItem.nQuantity * overallQuantity,
                            singlePrice: singlePrice,
                            totalPrice: totalPrice,
                            articleType: shopDataItem ? shopDataItem.articleType : null,
                            configurationAttributes: shopDataItem ? shopDataItem.configurationAttributes : null,
                            configurationDetails: ecsVarsItem ? ecsVarsItem.configurationDetails : null,
                            variationAttributesList: ecsVarsItem ? ecsVarsItem.variationAttributesList : null,
                            availabilityStatusMessage: shopDataItem ? (shopDataItem.availabilityStatusMessage || null) : null,
                            availabilityStatusColor: shopDataItem ? (shopDataItem.availabilityStatusColor || null) : null,
                            shopPosNumber: index,
                            bomArticles: shopDataItem ? shopDataItem.bomArticles : null,
                            error: shopDataItem || userNotLoggedIn ? null : self.hcs.t.gui("PartsList_NotInShop")
                        };
                    });

                    bomResult.items.forEach(bomItem => {
                        bomResult.totalPrice += bomItem.totalPrice;
                    });

                    // Resolve configurator custom overall quantity
                    // like there is a "quantity" attribute in configuration.
                    

                   // bomResult.totalPrice *= overallQuantity;

                    promiseProgressGate.pResolve(bomResult);
                };

                if(ecsGetBomResult.success) {
                    try {
                        let ecsBomObject = JSON.parse(ecsGetBomResult.ecs);

                        if(ecsBomObject.oBomData && ecsBomObject.oBomData.aGroupedDatasets &&
                            ecsBomObject.oBomData.aGroupedDatasets[0] && ecsBomObject.oBomData.aGroupedDatasets[0].aDatasets
                        ) {
                            // First convert ECS parts list object to Haefele shop request object.
                            let shopRequestObject = self.toShopRequestObject(ecsBomObject.oBomData.aGroupedDatasets[0].aDatasets.map((item, index) => {
                                return {
                                    shopPosNumber: index,
                                    productId: item.sArtNr,
                                    quantity: parseInt(item.nQuantity) * overallQuantity
                                };
                            }));

                            if(shopRequestObject.productIdentifierInformation.length > 0) {
                                self.hcs.externalFunction("SETTINGS.SHOP_GET_PRODUCT_INFORMATION", shopRequestObject).then(shopResult => {
                                    if(opts.logRequest) {
                                        try {
                                            self.hcs.api.callback({
                                                url: self.hcs.api.getUrl(`log/shop/${this.hcs.configurationId}/getProductInformation`),
                                                showErrorMessage: false,
                                                data: {
                                                    jsonIn: JSON.stringify(shopRequestObject),
                                                    jsonOut: JSON.stringify(shopResult || {})
                                                },
                                                method: "POST"
                                            });
                                        } catch(e) { }
                                    }

                                    let userNotLoggedIn = shopResult.messages[0].text === self.hcs.settings.get("SETTINGS.SHOP_GET_PRODUCT_INFORMATION_NOT_LOGGED_IN_MESSAGE");
                                    // We build our data object when shop returns "success" but also when the result says "user not logged-in".
                                    // In this case we have to use product descriptions from ECS and cannot display any prices.

                                    switch(true) {
                                        case shopResult.messages[0].type === "success":
                                        case userNotLoggedIn:
                                            if(shopResult.ProductInformations.find(item => item.articleType === "VARIATIONARTICLE")) {
                                                // For variation products first read all attributes from ECS
                                                // and then pass all those attribute to shop object so that
                                                // shop interface can calculate the correct price.
                                                self.hcs.renderer.configure("GetVariationAttributesList", null, null, {
                                                    getVariationAttributesList: {
                                                        priceQuery: false,
                                                        productDataAttributes: {
                                                            items: shopResult.ProductInformations.filter(shopItem => {
                                                                return shopItem.articleType === "VARIATIONARTICLE";
                                                            }).map(shopItem => {
                                                                return objectAssignDeep(shopItem, {
                                                                    productId: shopItem.articleNumberFormatted
                                                                });
                                                            })
                                                        }
                                                    },
                                                    success: (getVarResult) => {
                                                        if(getVarResult.success) {
                                                            let ecsVarResult = JSON.parse(getVarResult.ecs);

                                                            ecsVarResult.result.forEach(ecsVarItem => {
                                                                shopRequestObject.productIdentifierInformation[ecsVarItem.rowIndex].variationAttributesList = objectAssignDeep(ecsVarItem.variationAttributesList);
                                                            });

                                                            if(opts.priceRequest) {
                                                                self.hcs.externalFunction("SETTINGS.SHOP_GET_PRODUCT_INFORMATION", shopRequestObject).then(shopResultWithPricing => {
                                                                    funcBuildPartsList(ecsBomObject, shopResultWithPricing, ecsVarResult.result, userNotLoggedIn);
                                                                });
                                                            } else {
                                                                funcBuildPartsList(ecsBomObject, shopResult, ecsVarResult.result, userNotLoggedIn);
                                                            }
                                                        }
                                                    }
                                                });
                                            } else {
                                                funcBuildPartsList(ecsBomObject, shopResult, null, userNotLoggedIn);
                                            }
                                            break;

                                        default:
                                            bomResult.success = false;
                                            bomResult.error = {
                                                message: "Could not read product information from shop: " + shopResult.messages[0].text,
                                                details: shopResult.messages[0].type
                                            };

                                            promiseProgressGate.pReject(bomResult);
                                            break;
                                    }
                                }).catch(e => {
                                    bomResult.success = false;
                                    bomResult.error = {
                                        message: e.message,
                                        details: e.stack
                                    };

                                    promiseProgressGate.pReject(bomResult);
                                });
                            } else {
                                promiseProgressGate.pResolve(bomResult);
                            }
                        } else {
                            promiseProgressGate.pResolve(bomResult);
                        }
                    } catch(error) {
                        if(opts.showSpinner) {
                            self.progressBar.progress(false);
                            loading = false;
                        }

                        bomResult.success = false;
                        bomResult.error = {
                            message: error.message,
                            details: error.stack
                        };

                        promiseProgressGate.pReject(bomResult);
                    }
                } else {
                    if(opts.showSpinner) {
                        self.progressBar.progress(false);
                        loading = false;
                    }

                    promiseProgressGate.pReject(ecsGetBomResult);
                }
            });
        });
    }

    /**
     * Load product data from local database.
     * @param {object} shopRequestObject - JSON structure which contains product IDs and quantity. The request object is the same than
     * the one which must be send to Haefele shop.
     * @returns {Promise} - Promise object. The response object is the same than the one from Haefele shop.
     */
    getLocalProductData(shopRequestObject) {
        let self = this;

        return new Promise((resolve) => {
            let requestData = {
                currency: self.hcs.cart.currency,
                products: shopRequestObject.productIdentifierInformation.map((item) => {
                    return {
                        productId: item.articleNumber,
                        quantity: item.requestedQuantity,
                        priceType: item.priceType,
                        shopPosNumber: item.shopPosNumber
                    };
                })
            };
            
            self.hcs.api.callback({
                url: self.hcs.api.getUrl(`/products/${self.hcs.sectionId}/${self.hcs.cultureId}`),
                data: requestData,
                method: "POST"
            }).then(result => {
                let response = {};

                if(result.success) {
                    response.messages = [
                        {
                            type: "success"
                        }
                    ];

                    response.ProductInformations = result.products.map(product => {
                        return {
                            shopPosNumber: product.shopPosNumber,
                            totalPrice: {
                                formatted: result.currency + " " + product.totalPrice,
                                priceType: "SALES_PRICE",
                                currency: result.currency,
                                value: product.totalPrice
                            },
                            quantityUnit: product.unit,
                            quantityUnitName: product.unit,
                            priceUnit: 100,
                            singlePrice: {
                                formatted: result.currency + " " + product.singlePrice,
                                priceType: "SALES_PRICE",
                                currency: result.currency,
                                value: product.singlePrice
                            },
                            articleNumber: product.productId,
                            articleNumberFormatted: product.productIdFormatted,
                            requestedQuantity: product.quantity * 100,
                            articleDisplayName: product.description,
                            articleDescription: product.descriptionDetail,
                            primaryImage: {
                                defaultUrl: self.hcs.api.getUrl("ConfigImage/icons/thumbs", product.productId) + `.jpg?bgcolor=${self.thumbnailSpecs.bgColor}`,
                                thumbnailUrl: self.hcs.api.getUrl("ConfigImage/icons/thumbs", product.productId) + `.jpg?size=${self.thumbnailSpecs.width}x${self.thumbnailSpecs.height}&bgcolor=${ self.thumbnailSpecs.bgColor }`
                            },
                            articleType: product.productType,
                            configurationAttributes: product.variationAttributes ? JSON.parse(product.variationAttributes) : null,
                            availabilityStatusMessage: product.availabilityStatusMessage || null,
                            availabilityStatusColor: product.availabilityStatusColor || null,
                            shopPosNumber: product.shopPosNumber,
                            bomArticles: product.bomArticles ? JSON.parse(product.bomArticles) : null
                        };
                    });
                } else {
                    response.messages = [
                        {
                            type: result.error.details,
                            text: result.error.message
                        }
                    ];
                }

                resolve(response);
            });
        });
    }

    /**
     * Hide parts list dialog.
     */
    close() {
        this.hcs.api.instance().classList.remove("hcs-parts-list-visible");
        this.hcs.api.instance(".hcs-button-parts-list").classList.remove("hcs-checked");
    }

    /**
     * Remove all items from parts list.
     */
    clear() {
        this.instance(".hcs-tbl-body").innerHTML = "";
        this.instance(".hcs-tbl-trh").innerHTML = "";
        this.instance(".hcs-table-compact").innerHTML = "";

        this.scrollbarMain.update();
    }

    /**
     * Build HTML from given ECS object.
     * @param {Object} result - Result data object containing ECS BoM data and Haefele shop data.
     */
    update(result) {
        let self = this;

        let loading = true;

        setTimeout(_ => {
            if(loading) {
                self.progressBar.progress("indeterminate");
            }
        }, 200);

        this.clear();

        return new Promise((resolve, reject) => {
            if(result.items.length > 0) {
                let tBodyCompact = this.instance(".hcs-table-compact");
                let tBody = this.instance(".hcs-tbl-body");
                let tHead = this.instance(".hcs-tbl-trh");

                this.hcs.cfgEngine.partsListModifyContent({
                    tableDom: this.instance(".hcs-tbl"),
                    colConfig: [
                        { id: "index", headerText: "" },
                        { id: "article", headerText: this.hcs.t.gui("PartsList_TH_Article") },
                        { id: "specification", headerText: this.hcs.t.gui("PartsList_TH_Specification") },
                        { id: "pieces", headerText: this.hcs.t.gui("PartsList_TH_Pieces") },
                        { id: "cost-single", headerText: this.hcs.t.gui("PartsList_TH_Cost") },
                        { id: "cost-sum", headerText: this.hcs.t.gui("PartsList_TH_Cost_Sum") }
                    ]
                }).then(customPartsList => {
                    customPartsList.colConfig.forEach(colItem => {
                        let colDiv = document.createElement("div");

                        colDiv.setAttribute("colName", colItem.id);
                        colDiv.innerHTML = colItem.headerText;

                        tHead.appendChild(colDiv);
                    });

                    result.items.forEach((bomItem, rowIndex) => {
                        let rowDom = document.createElement("div");
                        let rowDomCompact = document.createElement("li");
                        let colDomCompactLeft = document.createElement("div");
                        let colDomCompactRight = document.createElement("div");

                        let compactContentLeft = "";
                        let compactContentRight = "";

                        customPartsList.colConfig.forEach((col) => {
                            let colDom = document.createElement("div");
                            let columnContent = "";

                            switch(col.id) {
                                case "index":
                                    columnContent = rowIndex + 1;
                                    compactContentLeft += "<span>" + (customPartsList.columnCreated(col.id, bomItem, rowIndex, columnContent) || columnContent) + "</span>";
                                    break;

                                case "article":
                                    let aProductId = `<span class="hcs-product-id">${self.formatProductId(bomItem.productId)}</span>`;
                                    columnContent = aProductId + bomItem.thumbnail;

                                    compactContentLeft += customPartsList.columnCreated(col.id, bomItem, rowIndex, bomItem.thumbnail) || bomItem.thumbnail;
                                    compactContentRight += customPartsList.columnCreated(col.id, bomItem, rowIndex, aProductId) || aProductId;
                                    break;

                                case "specification":
                                    columnContent = `<span class="hcs-item-desc">${(bomItem.displayName ||"")}</span><span class="hcs-item-details">${(bomItem.description || "")}</span>` + (bomItem.error ? `<span class="hcs-item-error">${bomItem.error}</span>` : "");

                                    if(bomItem.availabilityStatusMessage && bomItem.availabilityStatusMessage !== "") {
                                        let availColor = bomItem.availabilityStatusColor ? ` style="color: ${bomItem.availabilityStatusColor}"` : "";
                                        columnContent += `<span class="hcs-avail-status"${availColor}>${bomItem.availabilityStatusMessage}</span>`;
                                    }

                                    if((bomItem.bomArticles || []).length > 0) {
                                        columnContent += `<div class="hcs-bom-arts">
                                                <ul>${bomItem.bomArticles.map((ba, index) =>
                                                {
                                                    return `<li${index > (self.visibleBomItems - 1) ? ' style="display: none"' : ''}>
                                                            <span class="hcs-artnr">${ba.articleNumber}</span>
                                                            <span class="hcs-desc">${ba.articleName}</span>
                                                        </li>`;
                                                }).join("")
                                                }</ul>
                                                <div class="hcs-bom-arts-toggle${(bomItem.bomArticles.length <= self.visibleBomItems) ? ' hcs-hidden' : ''}">
                                                    <hr />
                                                    <a class="hcs-toggle-hide">
                                                        ${self.hcs.t.gui("PartsList_Details_Hide")}
                                                        <svg icon-id="icn-001-2-arrow-bold" version="2.0">
                                                            <use href="#icn-001-2-arrow-bold"></use>
                                                        </svg>
                                                    </a>
                                                    <a class="hcs-toggle-show">
                                                        ${self.hcs.t.gui("PartsList_Details_Show")}
                                                        <svg icon-id="icn-001-2-arrow-bold" version="2.0">
                                                            <use href="#icn-001-2-arrow-bold"></use>
                                                        </svg>
                                                    </a>
                                                    <hr />
                                                </div>
                                            </div>`;
                                    }

                                    compactContentRight += customPartsList.columnCreated(col.id, bomItem, rowIndex, columnContent) || columnContent;
                                    break;

                                case "pieces":
                                    columnContent = bomItem.quantity + " " + bomItem.unit;
                                    compactContentRight += `<span class="hcs-cost-pieces">` + (customPartsList.columnCreated(col.id, bomItem, rowIndex, columnContent) || columnContent) + "</span>";
                                    break;

                                case "cost-single":
                                    columnContent = bomItem.error ? "-" : self.hcs.api.round(bomItem.singlePrice, 2).toLocaleString(self.hcs.constants.cultureId, { minimumFractionDigits: 2 }) + " " + this.hcs.cart.currency;
                                    compactContentRight += `<span class="hcs-cost-single">` + (customPartsList.columnCreated(col.id, bomItem, rowIndex, columnContent) || columnContent) + "</span>";
                                    break;

                                case "cost-sum":
                                    columnContent = bomItem.error ? "-" : self.hcs.api.round(bomItem.totalPrice, 2).toLocaleString(self.hcs.constants.cultureId, { minimumFractionDigits: 2 }) + " " + this.hcs.cart.currency;
                                    compactContentRight += `<span class="hcs-cost-sum">` + this.hcs.t.gui("PartsList_TH_Cost_Sum") + ": " + (customPartsList.columnCreated(col.id, bomItem, rowIndex, columnContent) || columnContent) + "</span>";
                                    break;
                            }

                            colDom.innerHTML = customPartsList.columnCreated(col.id, bomItem, rowIndex, columnContent) || columnContent;

                            rowDom.appendChild(colDom);
                        });

                        colDomCompactLeft.innerHTML = compactContentLeft;
                        colDomCompactRight.innerHTML = compactContentRight;

                        rowDomCompact.appendChild(colDomCompactLeft);
                        rowDomCompact.appendChild(colDomCompactRight);

                        tBody.appendChild(rowDom);
                        tBodyCompact.appendChild(rowDomCompact);

                        rowDom.querySelectorAll("img[org]").forEach(item => {
                            item.addEventListener("click", _ => {
                                let imgItem = document.createElement("img");
                                imgItem.src = item.getAttribute("org");

                                self.hcs.api.imageView(imgItem, { className: "hcs-parts-list-image" });
                            });
                        });

                        rowDomCompact.querySelectorAll("img[org]").forEach(item => {
                            item.addEventListener("click", _ => {
                                let imgItem = document.createElement("img");
                                imgItem.src = item.getAttribute("org");

                                self.hcs.api.imageView(imgItem, { className: "hcs-parts-list-image" });
                            });
                        });
                    });

                    (this.instance(".hcs-toggle-hide") || []).forEach(toggleHide => {
                        toggleHide.addEventListener("click", _ => {
                            toggleHide.parentNode.parentNode.classList.remove("hcs-active");
                        });
                    });

                    (this.instance(".hcs-toggle-show") || []).forEach(toggleShow => {
                        toggleShow.addEventListener("click", _ => {
                            toggleShow.parentNode.parentNode.classList.add("hcs-active");
                        });
                    });

                    self.progressBar.progress(false);
                    loading = false;

                    resolve();
                }).catch(partsListModifyContentError => {
                    console.error(partsListModifyContentError);

                    self.hcs.api.showError({
                        title: "Could not read product information",
                        messageMap: "jsError",
                        message: typeof (result) === "string" ? {
                            message: partsListModifyContentError
                        } : result
                    });

                    reject();
                });
            }
        });
    }

    /**
     * Convert product number into another format.
     * The formatting options are defined in: SETTINGS.PRODUCT_ID_CONVERTER.
     * See "ParamNames" enum in CommonSettings class for detailed description.
     * @param {string} productId - Product ID to be formatted
     * @returns {string} Product ID formatted.
     */
    formatProductId(productId) {
        if(!this.productSplitOptions || (new RegExp(this.productSplitOptions.test)).exec(productId) === null) {
            return productId;
        }

        let match = new RegExp(this.productSplitOptions.split).exec(productId);

        if(!match) {
            console.warn(`Could not convert product id ${productId} because it does not match the specified regular expression ${this.productSplitOptions.split}`);
            return productId;
        }

        return match
            .filter((i, index) => { return index > 0; })
            .join(this.productSplitOptions.join);
    }

    /**
     * Select elements inside parts list container.
     * @param {string} [selector] - CSS selector to select elements. Leave empty to get the main container.
     * @returns {Object|Array} DOM element(s) for given selector.
     * Returns a single DOM element when selector matches excatly one item, otherwise an array of DOM elements.
     */
    instance(selector) {
        return this.hcs.api.instance(selector, this.container);
    }
}